import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { states } from '../../data/locations/states';
import { cities } from '../../data/locations/cities';
import { getAreasForCity } from '../../data/locations/areas';
import { useDebounce } from '../../hooks/useDebounce';

interface SearchResult {
  type: 'state' | 'city' | 'area' | 'pincode';
  state: string;
  city?: string;
  area?: string;
  label: string;
  value: string;
  pincode?: string;
}

export default function LocationSearch() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(search, 300);
  const resultsPerPage = 20;

  // Search function that includes pincode search
  const searchLocations = (term: string): SearchResult[] => {
    const searchResults: SearchResult[] = [];
    const normalizedTerm = term.toLowerCase();
    const isPincodeSearch = /^\d+$/.test(term); // Check if search term is numeric (pincode)

    // Search states
    states.forEach(state => {
      if (state.label.toLowerCase().includes(normalizedTerm)) {
        searchResults.push({
          type: 'state',
          state: state.value,
          label: state.label,
          value: state.value
        });
      }
    });

    // Search cities, areas, and pincodes
    states.forEach(state => {
      const stateCities = cities[state.value] || [];
      
      stateCities.forEach(city => {
        // Match city
        if (city.label.toLowerCase().includes(normalizedTerm)) {
          searchResults.push({
            type: 'city',
            state: state.value,
            city: city.value,
            label: `${city.label}, ${state.label}`,
            value: city.value
          });
        }

        // Match areas and pincodes
        const areas = getAreasForCity(city.value);
        areas.forEach(area => {
          // Match by area name
          if (area.label.toLowerCase().includes(normalizedTerm)) {
            searchResults.push({
              type: 'area',
              state: state.value,
              city: city.value,
              area: area.value,
              label: `${area.label}, ${city.label}, ${state.label}`,
              value: area.value,
              pincode: area.pincode
            });
          }

          // Match by pincode
          if (isPincodeSearch && area.pincode?.includes(term)) {
            searchResults.push({
              type: 'pincode',
              state: state.value,
              city: city.value,
              area: area.value,
              label: `${area.label} (${area.pincode}), ${city.label}, ${state.label}`,
              value: area.value,
              pincode: area.pincode
            });
          }
        });
      });
    });

    return searchResults;
  };

  useEffect(() => {
    if (!debouncedSearch) {
      setResults([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchResults = searchLocations(debouncedSearch);
      setResults(searchResults);
      setCurrentPage(1);

      if (searchResults.length === 0) {
        setError('No locations found matching your search');
      }
    } catch (err) {
      setError('An error occurred while searching');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationSelect = (result: SearchResult) => {
    let path = `/locations/${result.state}`;
    
    if (result.city) {
      path += `/${result.city}`;
      if (result.area) {
        path += `/area/${result.area}`;
      }
    }
    
    navigate(path);
    setSearch('');
    setIsOpen(false);
  };

  const paginatedResults = results.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );

  const totalPages = Math.ceil(results.length / resultsPerPage);

  return (
    <div className="max-w-3xl mx-auto px-4">
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder="Search by area, city, state or pincode..."
            className="w-full px-12 py-4 text-lg border rounded-lg shadow-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>

        {isLoading && (
          <div className="absolute inset-x-0 top-full mt-2 p-4 bg-white rounded-lg shadow-lg text-center">
            <div className="animate-pulse">Searching...</div>
          </div>
        )}

        {error && (
          <div className="absolute inset-x-0 top-full mt-2 p-4 bg-red-50 text-red-600 rounded-lg shadow-lg">
            {error}
          </div>
        )}

        {isOpen && !isLoading && !error && paginatedResults.length > 0 && (
          <div className="absolute inset-x-0 top-full mt-2 bg-white rounded-lg shadow-lg overflow-hidden z-50">
            <ul className="divide-y divide-gray-200">
              {paginatedResults.map((result, index) => (
                <li key={`${result.type}-${result.value}-${index}`}>
                  <button
                    onClick={() => handleLocationSelect(result)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start space-x-3"
                  >
                    <MapPin className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">{result.label}</p>
                      {result.pincode && result.type !== 'pincode' && (
                        <p className="text-sm text-gray-500">Pincode: {result.pincode}</p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            {totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}